import React, { useEffect, useRef, useState } from "react";
import {
  Button,
  CircularProgress,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Grid
} from "@mui/material";
import { useSnackbar } from "notistack";
import { useSelector } from "react-redux";
import SettingNav from "../lease_management/elements/SettingNav";
import BackToLeaseList from "../lease_management/elements/BackToLeaseList";
import { NoRecordFound, Pagination } from "../shared_elements";
import TokenAdminAccess from "../hoc/TokenAdminAccess";
import { getGlobalCookie } from "../utils";
import { MockApi } from "./api/mockApi";
import {
  AI_CONSENT_POLICY_LINKS,
  AI_CONSENT_PROVIDER,
  formatProviderDisplayName,
  normalizeConsentHistoryPage,
  normalizeConsentStatus,
  normalizeConsentUserDetail
} from "./consentConstants";
import { trackActivity } from "../utils/mixpanel";
import "./consentAdmin.css";

const DEFAULT_PAGE_SIZE = 50;

function formatDate(value) {
  if (!value) return "—";
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return "—";
  return new Intl.DateTimeFormat("en", {
    day: "2-digit",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit"
  }).format(parsed);
}

function statusClass(label) {
  const value = String(label || "").toLowerCase();
  if (value.includes("re-accept") || value.includes("reaccept")) return "reaccept";
  if (value.includes("not")) return "not-accepted";
  if (value.includes("accept")) return "accepted";
  return "unknown";
}

function getCurrentUserContext() {
  let lessorAccess = null;
  let userName = null;
  try {
    lessorAccess = JSON.parse(getGlobalCookie("lessorAccess") || "null");
  } catch {
    lessorAccess = null;
  }
  try {
    userName = JSON.parse(getGlobalCookie("userName") || "null");
  } catch {
    userName = null;
  }

  return {
    userId: lessorAccess?.user?.id || lessorAccess?.user_id || null,
    userDisplayName:
      userName?.name ||
      [lessorAccess?.user?.first_name, lessorAccess?.user?.last_name]
        .filter(Boolean)
        .join(" ")
        .trim() ||
      lessorAccess?.user?.email ||
      "Current User",
    organisationName: lessorAccess?.name || lessorAccess?.lessor_name || "SPARTA"
  };
}

export function ConsentUsers() {
  const { enqueueSnackbar } = useSnackbar();
  const { permission } = useSelector((state) => state.leases);
  const historyRef = useRef(null);
  const historyLoadedRef = useRef(false);
  const currentUser = getCurrentUserContext();
  const [detailLoading, setDetailLoading] = useState(true);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [historyOpen, setHistoryOpen] = useState(false);
  const [detail, setDetail] = useState(null);
  const [historyQuery, setHistoryQuery] = useState({
    page: 1,
    page_size: DEFAULT_PAGE_SIZE
  });
  const [historyData, setHistoryData] = useState({
    list: [],
    pagination: {
      page: 1,
      per_page: DEFAULT_PAGE_SIZE,
      total: 0,
      total_pages: 1
    }
  });

  async function loadDetail() {
    setDetailLoading(true);
    try {
      let record = null;
      if (currentUser.userId) {
        try {
          record = normalizeConsentUserDetail(
            await MockApi.loadConsentUser(currentUser.userId)
          );
        } catch {
          record = null;
        }
      }

      if (!record) {
        const status = normalizeConsentStatus(await MockApi.loadConsentStatus());
        const acceptance = status.acceptance || {};
        record = normalizeConsentUserDetail({
          ...status,
          ...acceptance,
          user_id: currentUser.userId,
          user_name: currentUser.userDisplayName,
          accepted_versions: status.accepted_versions || acceptance.accepted_versions
        });
      }

      setDetail({
        ...record,
        user_name: record.user_name || currentUser.userDisplayName,
        organisation_name:
          record.organisation_name ||
          permission?.lessor?.name ||
          currentUser.organisationName
      });
    } catch (error) {
      setDetail(null);
      enqueueSnackbar(error.message || "Unable to load your AI consent details.", {
        variant: "error"
      });
    } finally {
      setDetailLoading(false);
    }
  }

  async function loadHistory(nextQuery = {}) {
    if (!currentUser.userId) {
      setHistoryData({
        list: [],
        pagination: {
          page: 1,
          per_page: DEFAULT_PAGE_SIZE,
          total: 0,
          total_pages: 1
        }
      });
      setHistoryLoading(false);
      return;
    }

    setHistoryLoading(true);
    try {
      const response = await MockApi.loadConsentUserHistory(currentUser.userId, {
        page: nextQuery.page ?? historyQuery.page,
        pageSize: nextQuery.page_size ?? historyQuery.page_size
      });
      const normalized = normalizeConsentHistoryPage(
        response,
        nextQuery.page ?? historyQuery.page,
        nextQuery.page_size ?? historyQuery.page_size
      );
      setHistoryData(normalized);
      setHistoryQuery({
        page: normalized.pagination.page,
        page_size: normalized.pagination.per_page
      });
    } catch (error) {
      setHistoryData({
        list: [],
        pagination: {
          page: 1,
          per_page: historyQuery.page_size,
          total: 0,
          total_pages: 1
        }
      });
      enqueueSnackbar(error.message || "Unable to load acceptance history.", {
        variant: "error"
      });
    } finally {
      setHistoryLoading(false);
    }
  }

  useEffect(() => {
    trackActivity("Page Viewed", {
      page_title: "AI Chat Assistant Consent User Details"
    });
    loadDetail();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  async function toggleHistory() {
    const nextOpen = !historyOpen;
    setHistoryOpen(nextOpen);
    trackActivity("Item Clicked", {
      page_title: "AI Chat Assistant Consent User Details",
      page_type: nextOpen ? "View Acceptance History" : "Hide Acceptance History"
    });

    if (nextOpen) {
      if (!historyLoadedRef.current) {
        await loadHistory({ page: 1, page_size: DEFAULT_PAGE_SIZE });
        historyLoadedRef.current = true;
      }
      window.setTimeout(() => {
        historyRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
      }, 50);
    }
  }

  const termsVersion =
    detail?.terms_version || detail?.accepted_versions?.terms_version || "—";
  const dataPolicyVersion =
    detail?.data_policy_version ||
    detail?.accepted_versions?.data_policy_version ||
    "—";
  const privacyVersion =
    detail?.privacy_policy_version ||
    detail?.accepted_versions?.privacy_policy_version ||
    "—";

  return (
    <Paper
      className="setting_details_section lease-details-section consent-admin-page"
      elevation={0}
      sx={{ boxShadow: "0px 0px 2px 1px #BBBBBB" }}
    >
      <SettingNav />
      <div className="lease-details-content">
        <Paper square className="responsive-paper">
          <Grid container spacing={1} className="responsive-grid" alignItems="flex-end">
            <Grid item size={{ md: 4, xs: 12 }} className="menu-container" marginTop="14px">
              <BackToLeaseList />
            </Grid>
          </Grid>
        </Paper>

        <div className="lease-detail-table consent-self-layout">
          <Paper className="consent-self-card consent-self-header-card" square>
            <div className="consent-user-summary">
              <strong>{detail?.user_name || currentUser.userDisplayName}</strong>
              <span>
                {detail?.organisation_name || currentUser.organisationName}
              </span>
            </div>
          </Paper>

          <Paper className="consent-self-card" square>
            <div className="consent-card-title-row">
              <div>
                <h3>AI Chat Assistant Consent</h3>
                <p className="consent-provider-copy">
                  Provider disclosed at acceptance:{" "}
                  {formatProviderDisplayName(
                    detail?.provider_display_name ||
                      AI_CONSENT_PROVIDER.provider_display_name
                  )}
                </p>
              </div>
              <span
                className={`consent-status-pill ${statusClass(
                  detail?.status_label || (detail?.accepted ? "Accepted" : "Not Accepted")
                )}`}
              >
                {detail?.status_label || (detail?.accepted ? "Accepted" : "Not Accepted")}
              </span>
            </div>

            {detailLoading ? (
              <div className="consent-admin-loading">
                <CircularProgress size={24} />
                Loading your consent details
              </div>
            ) : detail ? (
              <>
                <div className="consent-self-detail-grid consent-self-detail-grid--horizontal">
                  <div>
                    <span>Accepted on</span>
                    <strong>{formatDate(detail.accepted_at)}</strong>
                  </div>
                  <div>
                    <span>Terms of Use</span>
                    <strong>
                      {termsVersion !== "—" ? `v${String(termsVersion).replace(/^v/i, "")}` : "—"}
                    </strong>
                    <a
                      href={detail.terms_url || AI_CONSENT_POLICY_LINKS.termsOfUse}
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      View Terms
                    </a>
                  </div>
                  <div>
                    <span>AI &amp; Data Use</span>
                    <strong>
                      {dataPolicyVersion !== "—"
                        ? `v${String(dataPolicyVersion).replace(/^v/i, "")}`
                        : "—"}
                    </strong>
                    <a
                      href={detail.data_policy_url || AI_CONSENT_POLICY_LINKS.aiDataUse}
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      View AI &amp; Data Use
                    </a>
                  </div>
                  <div>
                    <span>Privacy Policy</span>
                    <strong>
                      {privacyVersion !== "—" ? `Ref: ${privacyVersion}` : "—"}
                    </strong>
                    <a
                      href={
                        detail.privacy_policy_url || AI_CONSENT_POLICY_LINKS.privacyPolicy
                      }
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      View Privacy Policy
                    </a>
                  </div>
                </div>
                <div className="consent-self-actions">
                  <Button
                    variant="contained"
                    className="btn-primary"
                    onClick={toggleHistory}
                  >
                    {historyOpen ? "Hide Acceptance History" : "View Acceptance History"}
                  </Button>
                </div>
              </>
            ) : (
              <NoRecordFound />
            )}
          </Paper>

          {historyOpen ? (
          <Paper
            ref={historyRef}
            className="consent-self-card consent-history-card"
            square
          >
            <div className="consent-card-title-row">
              <div>
                <h3>Acceptance Audit History</h3>
              </div>
            </div>

            {historyLoading ? (
              <div className="consent-admin-loading">
                <CircularProgress size={24} />
                Loading acceptance history
              </div>
            ) : historyData.list.length ? (
              <>
                <Table className="mui-table-format" stickyHeader>
                  <TableHead>
                    <TableRow>
                      <TableCell>Accepted at</TableCell>
                      <TableCell>User</TableCell>
                      <TableCell>Terms</TableCell>
                      <TableCell>AI &amp; Data Use</TableCell>
                      <TableCell>Privacy ref</TableCell>
                      <TableCell>Source</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {historyData.list.map((item) => (
                      <TableRow key={item.id}>
                        <TableCell>{formatDate(item.accepted_at)}</TableCell>
                        <TableCell>{item.user_name || "—"}</TableCell>
                        <TableCell>
                          <div className="consent-version-cell">
                            <span>{item.terms_version || "—"}</span>
                            <a
                              href={item.terms_url || AI_CONSENT_POLICY_LINKS.termsOfUse}
                              target="_blank"
                              rel="noopener noreferrer"
                            >
                              View
                            </a>
                          </div>
                        </TableCell>
                        <TableCell>
                          <div className="consent-version-cell">
                            <span>{item.data_policy_version || "—"}</span>
                            <a
                              href={item.data_policy_url || AI_CONSENT_POLICY_LINKS.aiDataUse}
                              target="_blank"
                              rel="noopener noreferrer"
                            >
                              View
                            </a>
                          </div>
                        </TableCell>
                        <TableCell>
                          <div className="consent-version-cell">
                            <span>{item.privacy_policy_version || "—"}</span>
                            <a
                              href={
                                item.privacy_policy_url ||
                                AI_CONSENT_POLICY_LINKS.privacyPolicy
                              }
                              target="_blank"
                              rel="noopener noreferrer"
                            >
                              View
                            </a>
                          </div>
                        </TableCell>
                        <TableCell>
                          {item.acceptance_source === "SPARTA_AI_CHAT_ASSISTANT"
                            ? "AI Chat Assistant"
                            : item.acceptance_source || "—"}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>

                <Paper square elevation={0}>
                  <Pagination
                    pagination={historyData.pagination}
                    onPageChange={(event, newPage) => {
                      loadHistory({
                        page: newPage + 1,
                        page_size: historyData.pagination.per_page
                      });
                    }}
                    onRowsPerPageChange={(event) => {
                      const perPage = parseInt(event.target.value, 10) || DEFAULT_PAGE_SIZE;
                      loadHistory({ page: 1, page_size: perPage });
                    }}
                  />
                </Paper>
              </>
            ) : (
              <NoRecordFound />
            )}
          </Paper>
          ) : null}
        </div>
      </div>
    </Paper>
  );
}

export default TokenAdminAccess(ConsentUsers);
